import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Link,
  Typography,
  IconButton,
} from '@mui/material';
import MarketProLogo from '../../javascript/images/MarketProLogo.png'
import "../../stylesheets/chat_page.css"
import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { isMobileBrowser } from '../utils/user_agent';
import { useChatContext } from '../contexts/ChatContext';
import { defaultPaywallModalMessage, isDarkMode } from '../utils/constants';
import { PAYWALL_SURFACE_TYPES, buildPaywallModalContent } from '../utils/paywallMessaging';
import { trackPaywallEvent } from '../utils/paywallTracking';
import { conversionGateEventMetadata } from '../utils/conversionGateTracking';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';

const PaywallModal = ({
  appProps,
  userData,
  setShowPaywallModal,
  paywallMessage,
  setShowSignUpModal,
  sidebarOffset,
  conversionGateContext,
}) => {

  const navigate = useNavigate();
  const { resetChatState } = useChatContext();

  const [opacity, setOpacity] = useState(0)
  const [subscriberDialogOpen, setSubscriberDialogOpen] = useState(false)

  const resolvedConversionGateContext =
    conversionGateContext === undefined
      ? appProps?.conversionGateContext
      : conversionGateContext
  const normalizedPaywallMessage = String(paywallMessage || '').toLowerCase()
  const isFreeQuestionLimitPaywall =
    Boolean(resolvedConversionGateContext) ||
    normalizedPaywallMessage.includes('free question') ||
    normalizedPaywallMessage.includes('free post-signup question') ||
    normalizedPaywallMessage.includes('post-signup question')
  const isDailyLimitPaywall =
    !paywallMessage ||
    paywallMessage === defaultPaywallModalMessage ||
    normalizedPaywallMessage.includes('daily limit') ||
    isFreeQuestionLimitPaywall
  const paywallSurfaceType = isFreeQuestionLimitPaywall
    ? PAYWALL_SURFACE_TYPES.freeQuestionLimit
    : (isDailyLimitPaywall ? PAYWALL_SURFACE_TYPES.dailyLimit : PAYWALL_SURFACE_TYPES.featureGate)

  const isMobile = isMobileBrowser()

  useEffect(() => {
    setOpacity(1.0)
  }, [])

  const hasAccount = Boolean(
    userData?.user_account_info && Object.keys(userData.user_account_info).length > 0
  )
  const hasActiveSubscription = !!userData?.has_active_subscription
  const eligibleForProTrial =
    !hasAccount || (!hasActiveSubscription && userData?.eligible_for_pro_trial !== false)
  const proTrialDays = Number(userData?.pro_trial_days) > 0 ? Number(userData.pro_trial_days) : 7
  const eyebrowText = eligibleForProTrial
    ? `Incite Pro ${proTrialDays}-Day Trial`
    : "Incite Pro"
  const primaryCtaLabel = !hasAccount
    ? `Sign Up to Start ${proTrialDays}-Day Trial`
    : eligibleForProTrial
      ? `Start ${proTrialDays}-Day Free Trial`
      : "Upgrade to Incite Pro"

  const paywallContent = useMemo(() => buildPaywallModalContent({
    eligibleForProTrial,
    proTrialDays,
    isDailyLimitPaywall,
    surfaceType: paywallSurfaceType,
  }), [eligibleForProTrial, isDailyLimitPaywall, paywallSurfaceType, proTrialDays])

  const descriptionText = paywallContent.descriptionText
  const modalBenefits = paywallContent.modalBenefits
  const paywallTrackingMetadata = useMemo(() => ({
    ...paywallContent.trackingMetadata,
    ...conversionGateEventMetadata(resolvedConversionGateContext),
    has_account: hasAccount,
    eligible_for_pro_trial: eligibleForProTrial,
    paywall_message: paywallMessage || defaultPaywallModalMessage,
  }), [resolvedConversionGateContext, eligibleForProTrial, hasAccount, paywallContent.trackingMetadata, paywallMessage])

  const handleClose = () => {
    trackPaywallEvent('paywall_modal_close', paywallTrackingMetadata)
    setShowPaywallModal(false); // Hide modal
    // setShowPaywallAfter(false); // won't default to showing after sign up modal close.
  };

  const onClickAction = () => {
    trackPaywallEvent('paywall_modal_cta_click', {
      ...paywallTrackingMetadata,
      cta_label: primaryCtaLabel,
    })
    if (hasAccount) {
      navigate('/plan-selection')
    } else {
      setShowPaywallModal(false)
      setShowSignUpModal(true)
    }
  }

  const loginEmail = userData?.user_account_info?.email
  const loginMethod = userData?.user_account_info?.login_method

  useEffect(() => {
    trackPaywallEvent('paywall_modal_view', paywallTrackingMetadata)
  }, [paywallTrackingMetadata])

  return (
    <div className="modal modal-paywall" style={{
      opacity: opacity,
      transition: 'opacity 0.35s ease',
      backgroundColor: isDarkMode ? 'rgba(2, 6, 14, 0.72)' : 'rgba(10, 24, 48, 0.32)',
      zIndex: 1600,
      }}>
      <div className="paywall-modal-content" style={{
        marginLeft: (!isMobile) ? `${sidebarOffset/2}px` :'unset',
        width: '500px',
        maxWidth: 'calc(100% - 32px)',
        maxHeight: 'calc(100vh - 32px)',
        padding: 0,
        overflowX: 'hidden',
        overflowY: 'auto',
        borderRadius: '24px',
        border: isDarkMode ? '1px solid rgba(62,84,118,0.55)' : '1px solid rgba(163,189,233,0.55)',
        background: isDarkMode
          ? 'linear-gradient(180deg, #131A28 0%, #0D111B 100%)'
          : 'linear-gradient(180deg, #FFFFFF 0%, #F4F8FF 100%)',
        boxShadow: isDarkMode
          ? '0 30px 90px rgba(0,0,0,0.56)'
          : '0 30px 90px rgba(31, 88, 174, 0.24)',
      }}>
        <Box
          sx={{
            p: { xs: 2.25, sm: 3 },
            pr: { xs: 5.5, sm: 6 },
            position: 'relative',
            textAlign: 'left',
            borderBottom: `1px solid ${isDarkMode ? 'rgba(68,85,112,0.45)' : 'rgba(187,208,243,0.65)'}`,
            background: isDarkMode
              ? 'radial-gradient(120% 120% at 100% 0%, rgba(39,129,255,0.3) 0%, rgba(17,27,43,0) 62%)'
              : 'radial-gradient(130% 130% at 100% 0%, rgba(47,127,255,0.2) 0%, rgba(255,255,255,0) 62%)',
          }}
        >
          <IconButton
            onClick={handleClose}
            aria-label="Close upgrade dialog"
            sx={{
              position: 'absolute',
              top: 12,
              right: 12,
              color: isDarkMode ? '#D5E4FF' : '#23529A',
            }}
          >
            <CloseRoundedIcon />
          </IconButton>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box
              component="img"
              src={MarketProLogo}
              className="logo-img"
              alt="Incite AI"
              sx={{ width: 30, height: 30, flexShrink: 0 }}
            />
            <Typography
              sx={{
                fontSize: '11px',
                textTransform: 'uppercase',
                letterSpacing: 1.2,
                fontWeight: 700,
                color: isDarkMode ? '#94B8F6' : '#2D66B7',
              }}
            >
              {eyebrowText}
            </Typography>
          </Box>

          <Typography
            sx={{
              mt: 1,
              fontSize: { xs: '23px', sm: '28px' },
              lineHeight: 1.08,
              fontWeight: 750,
              color: isDarkMode ? '#F3F8FF' : '#0F294F',
              fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
            }}
          >
            {paywallMessage || defaultPaywallModalMessage}
          </Typography>
          <Typography
            sx={{
              mt: 1.1,
              fontSize: '14px',
              lineHeight: 1.6,
              color: isDarkMode ? '#A5BEDF' : '#335B8F',
              maxWidth: 430,
            }}
          >
            {descriptionText}
          </Typography>
        </Box>

        <Box sx={{ p: { xs: 2.25, sm: 3 }, display: 'grid', gap: 1.15, textAlign: 'left' }}>
          {modalBenefits.map(({ text, centered }) => (
            <Box
              key={text}
              sx={{
                p: 1.25,
                borderRadius: 2,
                border: `1px solid ${isDarkMode ? 'rgba(85,105,138,0.4)' : 'rgba(176,201,239,0.62)'}`,
                backgroundColor: isDarkMode ? 'rgba(18,27,42,0.62)' : 'rgba(247,251,255,0.95)',
                display: 'grid',
                gridTemplateColumns: centered ? '22px minmax(0, 1fr) 22px' : '22px minmax(0, 1fr)',
                gap: 1,
                alignItems: 'center',
              }}
            >
              <Box
                sx={{
                  minWidth: 22,
                  height: 22,
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#fff',
                  background: 'linear-gradient(135deg, #2787FF 0%, #1A61E6 100%)',
                  mt: centered ? 0 : 0.15,
                }}
              >
                <CheckRoundedIcon sx={{ fontSize: 15 }} />
              </Box>
              <Typography
                sx={{
                  fontSize: '13px',
                  lineHeight: 1.55,
                  color: isDarkMode ? '#E5EEFF' : '#123A67',
                  textAlign: centered ? 'center' : 'left',
                }}
              >
                {text}
              </Typography>
              {centered && <Box aria-hidden="true" sx={{ width: 22, height: 22 }} />}
            </Box>
          ))}

          <Button
            variant="contained"
            onClick={() => onClickAction()}
            sx={{
              textTransform: 'none',
              mt: 0.75,
              height: 48,
              borderRadius: '999px',
              fontSize: '15px',
              fontWeight: 700,
              background: 'linear-gradient(135deg, #2787FF 0%, #1A61E6 100%)',
              boxShadow: '0 14px 30px rgba(25, 93, 210, 0.28)',
              '&:hover': {
                background: 'linear-gradient(135deg, #2F90FF 0%, #1C6AF4 100%)',
              },
            }}
          >
            {primaryCtaLabel}
          </Button>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              mt: 0.75,
            }}
          >
            <Button
              variant="text"
              size="small"
              onClick={() => setSubscriberDialogOpen(true)}
              sx={{
                alignSelf: { xs: 'flex-start', sm: 'center' },
                textTransform: 'none',
                fontSize: '12px',
                fontWeight: 700,
                lineHeight: '150%',
                p: 0,
                minWidth: 0,
                color: isDarkMode ? '#9BC3FF' : '#1D66C7',
              }}
            >
              Already a subscriber?
            </Button>
          </Box>
        </Box>

        <Dialog
          open={subscriberDialogOpen}
          onClose={() => setSubscriberDialogOpen(false)}
          aria-labelledby="subscriber-help-title"
          fullWidth
          maxWidth="xs"
          PaperProps={{
            sx: {
              borderRadius: 3,
              background: isDarkMode ? '#101724' : '#FFFFFF',
              color: isDarkMode ? '#F3F8FF' : '#0F294F',
            },
          }}
        >
          <DialogTitle id="subscriber-help-title" sx={{ fontWeight: 600 }}>
            Already a Subscriber
          </DialogTitle>
          <DialogContent dividers sx={{ borderColor: isDarkMode ? 'rgba(85,105,138,0.35)' : 'rgba(176,201,239,0.55)' }}>
            <Box className="base-text" sx={{ fontSize: '14px', lineHeight: '160%', color: isDarkMode ? '#C7D8F2' : '#2D4F78' }}>
              This account is under <b>{loginEmail}</b> using the <b>{loginMethod}</b> login method.
              <br /><br />
              The account you are currently logged into is not linked to an active subscription.
              <br /><br />
              Please try logging back in with the correct email and login method (review the subscription confirmation email you received), or contact
              Incite Support at{' '}
              <Link href="mailto:help@inciteai.com" underline="hover">
                help@inciteai.com
              </Link>{' '}
              for further assistance.
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setSubscriberDialogOpen(false)}>Close</Button>
            <Button
              onClick={() => {
                fetch("/logout", {
                  method: "POST",
                  headers: { "Content-Type": "application/json" },
                }).then(() => {
                  setShowPaywallModal(false)
                  setSubscriberDialogOpen(false)
                  appProps.clearStateData();
                  resetChatState();
                  navigate("/register");
                })
              }}
              variant="contained"
            >
              Log Out
            </Button>
          </DialogActions>
        </Dialog>

      </div>
    </div>
  )
}

export default PaywallModal
